import React, { useState, useMemo, useCallback } from 'react';
import BarChart, { BarChartData, BarChartAnswerState } from './BarChart';
import PieChart, { PieChartData, PieChartAnswerState } from './PieChart';
import PatternBuilder, { PatternConfig } from './PatternBuilder';
import MultipleChoice, { MultipleChoiceAnswerState } from './MultipleChoice';
import CoordinateGrid, { GridPoint } from './CoordinateGrid';
import LessonHeader, { MasteryQuestion } from './LessonHeader';
import ExerciseFooter, { FooterState } from './ExerciseFooter';

// ============ TYPES ============

type QuestionType =
  | 'bar-select'
  | 'bar-multi-select'
  | 'pie-select'
  | 'multiple-choice'
  | 'pattern-select'
  | 'coordinate-place';

interface PatternOption {
  config: PatternConfig;
  isCorrect: boolean;
}

interface QuestionNode {
  id: string;
  type: 'question';
  category: string;
  questionText: string;
  questionType: QuestionType;
  // Bar chart specific
  barData?: BarChartData[];
  barScaleMax?: number;
  barScaleStep?: number;
  // Pie chart specific
  pieData?: PieChartData[];
  pieLabelType?: 'label' | 'percentage' | 'both' | 'none';
  // Multiple choice specific
  multipleChoiceOptions?: (string | number)[];
  // Pattern specific
  patternConfig?: PatternConfig;
  patternOptions?: PatternOption[];
  // Coordinate grid specific
  gridRange?: number;
  targetCoordinates?: { x: number; y: number };
  // Common
  correctAnswer?: string | number | string[];
  correctIndices?: number[];
  explanation?: string;
  xpReward?: number;
}

// ============ QUESTION DATA - 3 Questions per Component Type ============

const QUESTIONS: QuestionNode[] = [
  // ========== BAR CHART QUESTIONS (3) ==========
  {
    id: 'bar-1',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which Australian state has the most cafes?',
    questionType: 'bar-select',
    barData: [
      { label: 'NT', value: 25, color: '#f59e0b' },
      { label: 'TAS', value: 65, color: '#8b5cf6' },
      { label: 'SA', value: 180, color: '#ef4444' },
      { label: 'WA', value: 320, color: '#3b82f6' },
      { label: 'QLD', value: 480, color: '#06b6d4' },
      { label: 'VIC', value: 720, color: '#10b981' },
    ],
    barScaleMax: 800,
    barScaleStep: 100,
    correctAnswer: 'VIC',
    correctIndices: [5],
    explanation: 'Victoria (VIC) has the tallest bar at about 720 cafes, making it the state with the most cafes.',
    xpReward: 15,
  },
  {
    id: 'bar-2',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which two drinks combined have about 200 sales?',
    questionType: 'bar-multi-select',
    barData: [
      { label: 'Mocha', value: 90, color: '#f59e0b' },
      { label: 'Latte', value: 120, color: '#8b5cf6' },
      { label: 'Espresso', value: 260, color: '#10b981' },
      { label: 'Americano', value: 370, color: '#0ea5e9' },
    ],
    barScaleMax: 400,
    barScaleStep: 100,
    correctAnswer: ['Mocha', 'Latte'],
    correctIndices: [0, 1],
    explanation: 'Mocha (90) + Latte (120) = 210, which is closest to 200.',
    xpReward: 20,
  },
  {
    id: 'bar-3',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which subject has about 75 students enrolled?',
    questionType: 'bar-select',
    barData: [
      { label: 'Art', value: 45, color: '#f97316' },
      { label: 'Music', value: 78, color: '#8b5cf6' },
      { label: 'Drama', value: 52, color: '#ef4444' },
      { label: 'PE', value: 95, color: '#10b981' },
    ],
    barScaleMax: 100,
    barScaleStep: 25,
    correctAnswer: 'Music',
    correctIndices: [1],
    explanation: 'Music has about 78 students, which is closest to 75.',
    xpReward: 15,
  },

  // ========== PIE CHART QUESTIONS (3) ==========
  {
    id: 'pie-1',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which coffee type makes up about half of all sales?',
    questionType: 'pie-select',
    pieData: [
      { label: 'Espresso', value: 45, color: '#10b981' },
      { label: 'Cappuccino', value: 25, color: '#8b5cf6' },
      { label: 'Latte', value: 20, color: '#3b82f6' },
      { label: 'Mocha', value: 10, color: '#f59e0b' },
    ],
    pieLabelType: 'both',
    correctAnswer: 'Espresso',
    correctIndices: [0],
    explanation: 'Espresso makes up 45% of sales, which is closest to half (50%).',
    xpReward: 15,
  },
  {
    id: 'pie-2',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which transport method is used by the smallest percentage of students?',
    questionType: 'pie-select',
    pieData: [
      { label: 'Bus', value: 35, color: '#0ea5e9' },
      { label: 'Car', value: 30, color: '#ef4444' },
      { label: 'Walk', value: 20, color: '#10b981' },
      { label: 'Bike', value: 15, color: '#f59e0b' },
    ],
    pieLabelType: 'percentage',
    correctAnswer: 'Bike',
    correctIndices: [3],
    explanation: 'Bike has the smallest slice at 15% of students.',
    xpReward: 15,
  },
  {
    id: 'pie-3',
    type: 'question',
    category: 'Data Analysis',
    questionText: 'Which two fruits together make up about 50% of the fruit bowl?',
    questionType: 'pie-select',
    pieData: [
      { label: 'Apple', value: 30, color: '#ef4444' },
      { label: 'Banana', value: 25, color: '#f59e0b' },
      { label: 'Orange', value: 25, color: '#f97316' },
      { label: 'Grape', value: 20, color: '#8b5cf6' },
    ],
    pieLabelType: 'both',
    correctAnswer: 'Banana',
    correctIndices: [1],
    explanation: 'Banana (25%) and Orange (25%) together make exactly 50%. Select Banana as it appears first.',
    xpReward: 15,
  },

  // ========== MULTIPLE CHOICE QUESTIONS (3) ==========
  {
    id: 'mc-1',
    type: 'question',
    category: 'Algebra',
    questionText: 'Solve for x: 2x + 5 = 13',
    questionType: 'multiple-choice',
    multipleChoiceOptions: [3, 4, 5, 6],
    correctAnswer: 4,
    explanation: '2x + 5 = 13 → 2x = 8 → x = 4',
    xpReward: 15,
  },
  {
    id: 'mc-2',
    type: 'question',
    category: 'Fractions',
    questionText: 'What is ¾ + ½?',
    questionType: 'multiple-choice',
    multipleChoiceOptions: ['1', '1¼', '1½', '2'],
    correctAnswer: '1¼',
    explanation: '¾ + ½ = ¾ + 2/4 = 5/4 = 1¼',
    xpReward: 15,
  },
  {
    id: 'mc-3',
    type: 'question',
    category: 'Geometry',
    questionText: 'How many sides does a hexagon have?',
    questionType: 'multiple-choice',
    multipleChoiceOptions: [5, 6, 7, 8],
    correctAnswer: 6,
    explanation: 'A hexagon has 6 sides. "Hexa" means six in Greek.',
    xpReward: 15,
  },

  // ========== PATTERN QUESTIONS (3) ==========
  {
    id: 'pattern-1',
    type: 'question',
    category: 'Patterns',
    questionText: 'How many squares will be in Step 5 of this pattern?',
    questionType: 'pattern-select',
    patternConfig: {
      baseSquares: [{ x: 0, y: 0 }],
      deltaSquares: [{ x: 0, y: 0 }],
      deltaOffset: { x: 1, y: 0 },
      stepsToShow: 4,
    },
    multipleChoiceOptions: [4, 5, 6, 7],
    correctAnswer: 5,
    explanation: 'The pattern adds 1 square each step: Step 1 = 1, Step 2 = 2, Step 3 = 3, Step 4 = 4, so Step 5 = 5.',
    xpReward: 15,
  },
  {
    id: 'pattern-2',
    type: 'question',
    category: 'Patterns',
    questionText: 'What is the rule for this growing pattern?',
    questionType: 'pattern-select',
    patternConfig: {
      baseSquares: [{ x: 0, y: 0 }, { x: 1, y: 0 }],
      deltaSquares: [{ x: 0, y: 0 }, { x: 1, y: 0 }],
      deltaOffset: { x: 2, y: 0 },
      stepsToShow: 3,
    },
    multipleChoiceOptions: ['+1 each step', '+2 each step', '+3 each step', '×2 each step'],
    correctAnswer: '+2 each step',
    explanation: 'Step 1 has 2 squares, Step 2 has 4, Step 3 has 6. The pattern adds 2 squares each step.',
    xpReward: 15,
  },
  {
    id: 'pattern-3',
    type: 'question',
    category: 'Patterns',
    questionText: 'This L-shaped pattern grows each step. How many squares in Step 4?',
    questionType: 'pattern-select',
    patternConfig: {
      baseSquares: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 0, y: 1 }],
      deltaSquares: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 0, y: 1 }],
      deltaOffset: { x: 1, y: 1 },
      stepsToShow: 3,
    },
    multipleChoiceOptions: [9, 10, 11, 12],
    correctAnswer: 12,
    explanation: 'Step 1 = 3, Step 2 = 6, Step 3 = 9. Adding 3 each step, Step 4 = 12.',
    xpReward: 15,
  },

  // ========== COORDINATE GRID QUESTIONS (3) ==========
  {
    id: 'coord-1',
    type: 'question',
    category: 'Coordinate Geometry',
    questionText: 'Place a point at the coordinates (3, 2).',
    questionType: 'coordinate-place',
    gridRange: 5,
    targetCoordinates: { x: 3, y: 2 },
    explanation: 'The coordinates (3, 2) mean 3 steps right and 2 steps up from the origin.',
    xpReward: 15,
  },
  {
    id: 'coord-2',
    type: 'question',
    category: 'Coordinate Geometry',
    questionText: 'Place a point at the coordinates (-2, 4).',
    questionType: 'coordinate-place',
    gridRange: 5,
    targetCoordinates: { x: -2, y: 4 },
    explanation: 'The coordinates (-2, 4) mean 2 steps left and 4 steps up from the origin.',
    xpReward: 15,
  },
  {
    id: 'coord-3',
    type: 'question',
    category: 'Coordinate Geometry',
    questionText: 'Place a point at the coordinates (1, -3).',
    questionType: 'coordinate-place',
    gridRange: 5,
    targetCoordinates: { x: 1, y: -3 },
    explanation: 'The coordinates (1, -3) mean 1 step right and 3 steps down from the origin.',
    xpReward: 15,
  },
];

// ============ COMPONENT ============

interface MixedLessonProps {
  onClose?: () => void;
}

const MixedLesson: React.FC<MixedLessonProps> = ({ onClose }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [streakCount, setStreakCount] = useState(0);

  // Answer states
  const [barAnswerStates, setBarAnswerStates] = useState<Record<string, BarChartAnswerState>>({});
  const [pieAnswerStates, setPieAnswerStates] = useState<Record<string, PieChartAnswerState>>({});
  const [mcAnswerStates, setMcAnswerStates] = useState<Record<string, MultipleChoiceAnswerState>>({});
  const [coordAnswerStates, setCoordAnswerStates] = useState<Record<string, { placed: GridPoint | null; isSubmitted: boolean; isCorrect?: boolean }>>({});

  const [completedQuestions, setCompletedQuestions] = useState<Set<string>>(new Set());
  const [incorrectAttempts, setIncorrectAttempts] = useState<Record<string, number>>({});
  const [showExplanation, setShowExplanation] = useState(false);

  // AI Buddy state
  const [showBuddyChat, setShowBuddyChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'buddy' | 'user'; text: string }>>([]);
  const [chatInput, setChatInput] = useState('');

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === QUESTIONS.length - 1;

  // Mastery questions for header - show ALL questions for practice mode
  const masteryQuestions = useMemo<MasteryQuestion[]>(() => {
    return QUESTIONS.map((q, qIndex) => {
      if (completedQuestions.has(q.id)) {
        const isCorrect = incorrectAttempts[q.id] === 0;
        return { state: isCorrect ? 'correct' as const : 'incorrect' as const };
      }
      if (qIndex === currentQuestionIndex) return { state: 'current' as const };
      return { state: 'pending' as const };
    });
  }, [currentQuestionIndex, completedQuestions, incorrectAttempts]);

  // Progress
  const progress = (currentQuestionIndex + 1) / QUESTIONS.length;

  // Footer state
  const footerState = useMemo<FooterState>(() => {
    if (completedQuestions.has(currentQuestion.id)) return 'correct';
    if ((incorrectAttempts[currentQuestion.id] || 0) > 0) return 'incorrect';
    return 'default';
  }, [currentQuestion, completedQuestions, incorrectAttempts]);

  // Check if answer is selected
  const hasSelection = useMemo(() => {
    const q = currentQuestion;
    if (q.questionType === 'bar-select' || q.questionType === 'bar-multi-select') {
      return (barAnswerStates[q.id]?.selectedIndices?.length || 0) > 0;
    }
    if (q.questionType === 'pie-select') {
      return (pieAnswerStates[q.id]?.selectedIndices?.length || 0) > 0;
    }
    if (q.questionType === 'multiple-choice' || q.questionType === 'pattern-select') {
      return mcAnswerStates[q.id]?.selectedOption !== null && mcAnswerStates[q.id]?.selectedOption !== undefined;
    }
    if (q.questionType === 'coordinate-place') {
      return coordAnswerStates[q.id]?.placed !== null;
    }
    return false;
  }, [currentQuestion, barAnswerStates, pieAnswerStates, mcAnswerStates, coordAnswerStates]);

  // Handle bar chart selection
  const handleBarSelection = useCallback((indices: number[]) => {
    setBarAnswerStates(prev => ({
      ...prev,
      [currentQuestion.id]: { selectedIndices: indices, isSubmitted: false },
    }));
  }, [currentQuestion]);

  // Handle pie chart selection
  const handlePieSelection = useCallback((indices: number[]) => {
    setPieAnswerStates(prev => ({
      ...prev,
      [currentQuestion.id]: { selectedIndices: indices, isSubmitted: false },
    }));
  }, [currentQuestion]);

  // Handle multiple choice selection
  const handleMcSelection = useCallback((option: string | number) => {
    setMcAnswerStates(prev => ({
      ...prev,
      [currentQuestion.id]: { selectedOption: option, isSubmitted: false },
    }));
  }, [currentQuestion]);

  // Handle coordinate grid placement
  const handleCoordPlacement = useCallback((point: GridPoint | null) => {
    setCoordAnswerStates(prev => ({
      ...prev,
      [currentQuestion.id]: { placed: point, isSubmitted: false },
    }));
  }, [currentQuestion]);

  // Check answer
  const checkAnswer = useCallback(() => {
    const q = currentQuestion;
    let isCorrect = false;

    if (q.questionType === 'bar-select') {
      const state = barAnswerStates[q.id];
      if (state?.selectedIndices?.length === 1) {
        const selectedLabel = q.barData?.[state.selectedIndices[0]]?.label;
        isCorrect = selectedLabel === q.correctAnswer;
      }
      setBarAnswerStates(prev => ({
        ...prev,
        [q.id]: { ...prev[q.id], isSubmitted: true, isCorrect },
      }));
    } else if (q.questionType === 'bar-multi-select') {
      const state = barAnswerStates[q.id];
      const selectedLabels = state?.selectedIndices?.map(i => q.barData?.[i]?.label) || [];
      const correctLabels = q.correctAnswer as string[];
      isCorrect = selectedLabels.length === correctLabels.length &&
        selectedLabels.every(l => correctLabels.includes(l as string));
      setBarAnswerStates(prev => ({
        ...prev,
        [q.id]: { ...prev[q.id], isSubmitted: true, isCorrect },
      }));
    } else if (q.questionType === 'pie-select') {
      const state = pieAnswerStates[q.id];
      if (state?.selectedIndices?.length === 1) {
        const selectedLabel = q.pieData?.[state.selectedIndices[0]]?.label;
        isCorrect = selectedLabel === q.correctAnswer;
      }
      setPieAnswerStates(prev => ({
        ...prev,
        [q.id]: { ...prev[q.id], isSubmitted: true, isCorrect },
      }));
    } else if (q.questionType === 'multiple-choice' || q.questionType === 'pattern-select') {
      const state = mcAnswerStates[q.id];
      isCorrect = state?.selectedOption === q.correctAnswer;
      setMcAnswerStates(prev => ({
        ...prev,
        [q.id]: { ...prev[q.id], isSubmitted: true, isCorrect },
      }));
    } else if (q.questionType === 'coordinate-place') {
      const state = coordAnswerStates[q.id];
      const target = q.targetCoordinates;
      isCorrect = state?.placed?.x === target?.x && state?.placed?.y === target?.y;
      setCoordAnswerStates(prev => ({
        ...prev,
        [q.id]: { ...prev[q.id], isSubmitted: true, isCorrect },
      }));
    }

    if (isCorrect) {
      setCompletedQuestions(prev => new Set([...prev, q.id]));
      setStreakCount(prev => prev + 1);
      setIncorrectAttempts(prev => ({ ...prev, [q.id]: 0 }));
    } else {
      setIncorrectAttempts(prev => ({ ...prev, [q.id]: (prev[q.id] || 0) + 1 }));
      setStreakCount(0);
    }

    setShowExplanation(true);
  }, [currentQuestion, barAnswerStates, pieAnswerStates, mcAnswerStates, coordAnswerStates]);

  // Continue to next question
  const handleContinue = useCallback(() => {
    if (!completedQuestions.has(currentQuestion.id) && hasSelection) {
      checkAnswer();
      return;
    }

    if (showExplanation) {
      setShowExplanation(false);
      if (isLastQuestion) {
        // Finish - close the modal
        onClose?.();
      } else {
        setCurrentQuestionIndex(prev => prev + 1);
        // Reset chat for new question
        setChatMessages([]);
        setShowBuddyChat(false);
      }
    }
  }, [currentQuestion, hasSelection, completedQuestions, checkAnswer, showExplanation, isLastQuestion, onClose]);

  // Generate hint based on question type
  const generateHint = useCallback((question: QuestionNode, hintLevel: number): string => {
    const hints: Record<string, string[]> = {
      'bar-select': [
        `Look at the heights of the bars. Which one reaches the highest point on the y-axis?`,
        `Compare each bar to the scale on the left side. The tallest bar represents the largest value.`,
        `The answer is the bar labeled "${question.correctAnswer}". Its bar reaches approximately ${question.barData?.find(d => d.label === question.correctAnswer)?.value} on the scale.`,
      ],
      'bar-multi-select': [
        `You need to find two bars. Add their values together - which pair gets closest to the target?`,
        `Try adding the smallest values first. Sometimes smaller numbers combined give you what you need.`,
        `The correct bars are ${Array.isArray(question.correctAnswer) ? question.correctAnswer.join(' and ') : question.correctAnswer}.`,
      ],
      'pie-select': [
        `Look at the size of each slice. Which slice takes up the portion mentioned in the question?`,
        `Compare the percentages shown. Remember that half means 50%, quarter means 25%.`,
        `The answer is "${question.correctAnswer}".`,
      ],
      'multiple-choice': [
        `Read the question carefully. What operation do you need to perform?`,
        `Work through the problem step by step. Write down each step if it helps.`,
        `The correct answer is ${question.correctAnswer}.`,
      ],
      'pattern-select': [
        `Count the squares in each step. What's the pattern of growth?`,
        `Find how many squares are added each time. Then continue the pattern.`,
        `The answer is ${question.correctAnswer}.`,
      ],
      'coordinate-place': [
        `Remember: the first number (x) tells you how far to go left or right. The second number (y) tells you how far to go up or down.`,
        `Start at the origin (0,0) where the axes cross. Move ${question.targetCoordinates?.x ?? 0 > 0 ? 'right' : 'left'} first, then ${question.targetCoordinates?.y ?? 0 > 0 ? 'up' : 'down'}.`,
        `The point should be at (${question.targetCoordinates?.x}, ${question.targetCoordinates?.y}).`,
      ],
    };

    const questionHints = hints[question.questionType] || hints['multiple-choice'];
    return questionHints[Math.min(hintLevel, questionHints.length - 1)];
  }, []);

  // Handle buddy chat
  const handleBuddyMessage = useCallback((message: string) => {
    const lowerMessage = message.toLowerCase();
    setChatMessages(prev => [...prev, { role: 'user', text: message }]);

    // Determine hint level based on conversation
    const hintCount = chatMessages.filter(m => m.role === 'buddy').length;

    let response: string;
    if (lowerMessage.includes('hint') || lowerMessage.includes('help') || lowerMessage.includes('stuck')) {
      response = generateHint(currentQuestion, hintCount);
    } else if (lowerMessage.includes('explain') || lowerMessage.includes('how')) {
      response = `This is a ${currentQuestion.category} question. ${generateHint(currentQuestion, 0)}`;
    } else if (lowerMessage.includes('answer') || lowerMessage.includes('tell me')) {
      response = `I can give you hints, but try working through it first! ${generateHint(currentQuestion, Math.min(hintCount, 1))}`;
    } else {
      response = `I'm here to help! Ask me for a hint if you're stuck, or tell me what's confusing you about this ${currentQuestion.category.toLowerCase()} question.`;
    }

    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: 'buddy', text: response }]);
    }, 500);
  }, [currentQuestion, chatMessages, generateHint]);

  // Try again
  const handleTryAgain = useCallback(() => {
    const q = currentQuestion;
    setIncorrectAttempts(prev => ({ ...prev, [q.id]: 0 }));
    setShowExplanation(false);

    if (q.questionType === 'bar-select' || q.questionType === 'bar-multi-select') {
      setBarAnswerStates(prev => ({ ...prev, [q.id]: { selectedIndices: [], isSubmitted: false } }));
    } else if (q.questionType === 'pie-select') {
      setPieAnswerStates(prev => ({ ...prev, [q.id]: { selectedIndices: [], isSubmitted: false } }));
    } else if (q.questionType === 'multiple-choice' || q.questionType === 'pattern-select') {
      setMcAnswerStates(prev => ({ ...prev, [q.id]: { selectedOption: null, isSubmitted: false } }));
    } else if (q.questionType === 'coordinate-place') {
      setCoordAnswerStates(prev => ({ ...prev, [q.id]: { placed: null, isSubmitted: false } }));
    }
  }, [currentQuestion]);

  // Render question content
  const renderQuestionContent = () => {
    const q = currentQuestion;

    if (q.questionType === 'bar-select' || q.questionType === 'bar-multi-select') {
      return (
        <BarChart
          data={q.barData || []}
          mode="interactive"
          maxSelections={q.questionType === 'bar-multi-select' ? 2 : 1}
          scaleMax={q.barScaleMax}
          scaleStep={q.barScaleStep}
          answerState={barAnswerStates[q.id] || { selectedIndices: [], isSubmitted: false }}
          correctIndices={q.correctIndices || []}
          onSelectionChange={handleBarSelection}
        />
      );
    }

    if (q.questionType === 'pie-select') {
      return (
        <PieChart
          data={q.pieData || []}
          mode="interactive"
          labelType={q.pieLabelType}
          answerState={pieAnswerStates[q.id] || { selectedIndices: [], isSubmitted: false }}
          correctIndices={q.correctIndices || []}
          onSelectionChange={handlePieSelection}
        />
      );
    }

    if (q.questionType === 'multiple-choice') {
      return (
        <MultipleChoice
          options={q.multipleChoiceOptions || []}
          answerState={mcAnswerStates[q.id] || { selectedOption: null, isSubmitted: false }}
          correctAnswer={q.correctAnswer as string | number}
          onSelectionChange={handleMcSelection}
        />
      );
    }

    if (q.questionType === 'pattern-select') {
      return (
        <div className="space-y-6">
          {q.patternConfig && (
            <PatternBuilder
              config={q.patternConfig}
              color="#0ea5e9"
              squareSize={18}
              showLabels={true}
            />
          )}
          <MultipleChoice
            options={q.multipleChoiceOptions || []}
            answerState={mcAnswerStates[q.id] || { selectedOption: null, isSubmitted: false }}
            correctAnswer={q.correctAnswer as string | number}
            onSelectionChange={handleMcSelection}
            columns={4}
          />
        </div>
      );
    }

    if (q.questionType === 'coordinate-place') {
      const state = coordAnswerStates[q.id];
      const isSubmitted = state?.isSubmitted || false;
      const placedPoints: GridPoint[] = state?.placed ? [{
        ...state.placed,
        id: 'user',
        color: isSubmitted ? (state.isCorrect ? '#10b981' : '#ef4444') : '#0ea5e9',
        draggable: !isSubmitted,
        showLabel: true,
      }] : [];

      return (
        <div className="flex justify-center">
          <CoordinateGrid
            size={320}
            gridRange={q.gridRange || 5}
            points={placedPoints}
            disabled={isSubmitted}
            allowPlacement={!isSubmitted}
            onPointMove={(id, pos) => handleCoordPlacement({ id, x: pos.x, y: pos.y })}
            onGridClick={(pos) => handleCoordPlacement({ id: 'user', x: pos.x, y: pos.y })}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        minHeight: '600px',
      }}
    >
      {/* Header */}
      <LessonHeader
        progress={progress}
        masteryQuestions={masteryQuestions}
        streakCount={streakCount}
        onClose={onClose}
        mode="practice"
      />

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl mx-auto">
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-4">
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ background: 'rgba(14, 165, 233, 0.2)', color: '#0ea5e9' }}
            >
              {currentQuestion.category}
            </span>
            <span className="text-xs" style={{ color: '#64748b' }}>
              Question {currentQuestionIndex + 1} of {QUESTIONS.length}
            </span>
          </div>

          {/* Question text */}
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#e2e8f0' }}>
            {currentQuestion.questionText}
          </h2>

          {/* Question content */}
          <div className="mb-6">
            {renderQuestionContent()}
          </div>

          {/* Explanation */}
          {showExplanation && currentQuestion.explanation && (
            <div
              className="p-4 rounded-xl mb-6"
              style={{
                background: completedQuestions.has(currentQuestion.id)
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${completedQuestions.has(currentQuestion.id) ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              }}
            >
              <div className="flex items-start gap-3">
                <span className="text-lg">
                  {completedQuestions.has(currentQuestion.id) ? '✓' : '💡'}
                </span>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: completedQuestions.has(currentQuestion.id) ? '#10b981' : '#f59e0b' }}>
                    {completedQuestions.has(currentQuestion.id) ? 'Correct!' : 'Explanation'}
                  </p>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>
                    {currentQuestion.explanation}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <ExerciseFooter
        state={footerState}
        buttonText={completedQuestions.has(currentQuestion.id) ? (isLastQuestion ? 'Finish' : 'Continue') : 'Check'}
        buttonEnabled={hasSelection || completedQuestions.has(currentQuestion.id)}
        xpAmount={currentQuestion.xpReward || 15}
        isLastQuestion={isLastQuestion}
        onContinue={handleContinue}
        onTryAgain={handleTryAgain}
        onWhy={() => setShowExplanation(true)}
      />

      {/* AI Buddy Button */}
      {!showBuddyChat && !completedQuestions.has(currentQuestion.id) && (
        <button
          onClick={() => {
            setShowBuddyChat(true);
            if (chatMessages.length === 0) {
              setChatMessages([{
                role: 'buddy',
                text: `Hi! I'm here to help with this ${currentQuestion.category.toLowerCase()} question. Ask me for a hint if you get stuck!`
              }]);
            }
          }}
          className="fixed bottom-24 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)' }}
        >
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* AI Buddy Chat Panel */}
      {showBuddyChat && (
        <div
          className="fixed bottom-24 right-6 w-80 rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: 'rgba(30, 41, 59, 0.98)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          {/* Chat Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-lg">🤖</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Study Buddy</p>
                <p className="text-xs text-white/70">Here to help!</p>
              </div>
            </div>
            <button
              onClick={() => setShowBuddyChat(false)}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Chat Messages */}
          <div className="h-64 overflow-y-auto p-4 space-y-3">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-md'
                      : 'bg-slate-700 text-slate-200 rounded-bl-md'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    handleBuddyMessage(chatInput.trim());
                    setChatInput('');
                  }
                }}
                placeholder="Ask for a hint..."
                className="flex-1 px-3 py-2 rounded-xl text-sm bg-slate-700 text-white placeholder-slate-400 border border-slate-600 focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={() => {
                  if (chatInput.trim()) {
                    handleBuddyMessage(chatInput.trim());
                    setChatInput('');
                  }
                }}
                className="w-10 h-10 rounded-xl bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            {/* Quick hint buttons */}
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleBuddyMessage('Give me a hint')}
                className="px-3 py-1 rounded-full text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
              >
                Hint please
              </button>
              <button
                onClick={() => handleBuddyMessage('How do I solve this?')}
                className="px-3 py-1 rounded-full text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
              >
                How to solve?
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MixedLesson;
